import { SmartSearchModelField, SortDirection } from 'mbs-ui-kit';
import { QueryPagingParams } from 'mbs-ui-shared/src';
import { LicenseType } from 'mbs-ui-shared@models/LicenseType.enum';

export interface Money {
  currencyName: number;
  integerValue: number;
  remainderValue: number;
}

export interface PrePaymentResponse {
  data: Array<PrePaymentLicense>;
  limit: number;
  offset: number;
  total: number;
}

export type PrePaymentResponseGrouped = Array<PrePaymentGroup>;

export interface PrePaymentGroup {
  licenseType: LicenseTypeData;
  quantity: number;
  assigned: number;
  notAssigned: number;
  expired: number;
}

export interface LicenseOperationData {
  ids: Array<string>;
  filter: Partial<{
    search: string;
    companies: Array<string>;
    licenseTypes: string;
    statuses: Array<number>;
  }>;
  search?: LicenseOperationSearch;
}

export enum SearchField {
  Endpoint,
  User,
  PurchaseId
}

interface SearchFilterElement {
  field: SearchField;
  text: string;
}

export interface LicenseOperationSearch {
  searchFilterElements: Array<SearchFilterElement>;
  text: string;
}

export interface LicenseUsageBilling {
  unitName: string;
  value: string;
}

export enum SubscriptionStatus {
  None = 'None',
  Signed = 'Signed',
  Canceled = 'Canceled',
  WaitingSigned = 'WaitingSigned'
}

export enum LicensePrePaymentSort {
  ByPurchased = '6',
  ByActivated = '7',
  ByValidUntil = '8',
  ByCost = '10',
  ByCompany = '1',
  ByPool = '11'
}

export enum LicenseUsageHistorySort {
  ByLicenseType = '0',
  ByCompany = '1',
  ByUser = '2',
  ByEndpoint = '3',
  ByStatus = '4',
  ByPurchasedId = '5',
  ByPurchasedDate = '6',
  ByActivated = '7',
  ByDeactivated = '8',
  ByDaysActive = '9',
  ByPaid = '10'
}

export enum GroupedLicenseUsageHistorySort {
  ByCompany = '1',
  ByComputersNumber = '2',
  ByPaid = '10'
}

export enum LicenseUsageSort {
  ByLicenseName = 0,
  ByEndpoint = 1,
  ByCompany = 2,
  ByUserAccount = 3,
  ByType = 4,
  ByActivated = 5,
  ByReleased = 6,
  ByUnitPrice = 7,
  ByBillingToDate = 8,
  ByCostToDate = 9
}

export enum GroupType {
  Unknown,
  Company,
  AppsUser,
  AppsDomain
}

export enum LicenseStatus {
  Trial = 1,
  Paid = 2,
  Released = 3,
  Used = 4,
  Free = 5
}

export interface RenewResponse {
  credits: number;
  total: number;
  licensesInfo: Billing;
  storagesInfo: {
    wasabiDetails: Array<WasabiDetail>;
    details: Array<AwsDetail>;
  };
}

export interface WasabiDetail {
  detail: string;
  periodStart: string;
  periodEnd: string;
  unitCost: number;
  unitCostDetail: string;
  quantity: number;
  total: number;
}

export interface AwsDetail {
  displayName: string;
  periodStart: string;
  periodEnd: string;
  priceBy1GB: number;
  storageVolume: number;
  deletedDataSize: number;
  totalCost: number;
}

export interface Billing {
  price: string;
  discount: number;
  total: string;
  credits: string;
  currentCredits: number;
  creditPurchase: string;
  creditsLeft: string;
  subscriptionCredits: number;
  subscriptionCreditsExpireDate: Date | string;
  currentTotalCredits: number;
  subscriptionPurchase: string;
  periodStart: string;
  periodStop: string;
  licenses: Array<RenewLicense>;
  storagesInfo: {
    wasabiDetails: Array<WasabiDetail>;
    details: Array<AwsDetail>;
  };
  listPrice: number;
  proratedPrice: number;
  days: number;
  storageLicenses: null;
  totalPrice: number;
  totalDiscount: number;
  totalDiscountedPrice: number;
  minCommitment: number;
  freeCredits: number;
  freeCreditsExpireDate: Date | string;
}

export interface SearchLicenses {
  Endpoint: Array<SmartSearchModelField>;
  User: Array<SmartSearchModelField>;
  'Purchase ID': Array<SmartSearchModelField>;
  words: Array<SmartSearchModelField>;
}

export interface LicenseBilling {
  licenseType: LicenseTypeData;
  count: number;
  unitDiscountedPrice: number;
  price: number;
  totalPrice: string;
}

export interface Group {
  description: string;
  groupType: GroupType;
  value: string;
}

export enum EndpointType {
  Unknown = 0,
  Computer = 1,
  Administrator = 2,
  Provider = 3,
  Branding = 4,
  AppsDomain = 5, // O365/Google domain
  AppsUser = 6, // O365/Google user
  Storage = 7
}

export interface LicenseTypeDto {
  id: number;
  name: string;
  kind: number;
}

export interface LicenseUsageItem {
  id: number;
  ownerId: string;
  licenseType: LicenseTypeDto;
  startDate: string;
  stopDate: string;
  endpoint: EndpointLicenseDto;
  freeDays: number;
  isTrial: boolean;
  groups: Array<GroupDto>;
  price: Money;
  billing: LicenseUsageBilling;
  cost: Money;
  trialDays: number;
  canSetAutoRenewal: boolean;
  canManage: boolean;
  canRelease: boolean;
}

export interface LicenseTypeData {
  id: LicenseType;
  displayName: string;
}

export interface LicenseUsage {
  licenseTypeId: number;
  licenseTypeName: string;
  startDate: string;
  stopDate: string;
  isTrial: boolean;
  endpoint: EndpointDto;
}

export interface PrePaymentLicense {
  activated: string;
  assigned: boolean;
  autoRenewal: boolean;
  company: { id: number; name: string };
  cost: number;
  endpoint: { name: string };
  hid: string;
  id: string;
  licenseType: { displayName: string; id: LicenseType };
  pool: Pool;
  purchased: { date: string; id: string };
  status: { displayName: string; id: PrePaymentLicenseStatus };
  user: { id: number; name: string };
  validUntil: string;
}

export interface RenewLicense {
  licenseType: LicenseTypeData;
  quantity: number;
  unitProratedPrice: number;
  unitPrice: number;
  unitTotalPrice: number;
  lineItemPrice: number;
  discount: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
}

export enum PrePaymentLicenseStatus {
  TRIAL,
  PAID,
  EXPIRED,
  ACTIVE_TRIAL,
  EXPIRED_TRIAL
}

export enum AdditionalFilterValue {
  All = 'all',
  True = 'true',
  False = 'false'
}

export enum FilterBy {
  Expired,
  Assigned,
  NotAssigned,
  All,
  ActiveTrial,
  Paid,
  AutoRenewalDisabled,
  ExpiredTrial
}

export interface LicenseUsageItemDtoPage {
  data: Array<LicenseUsageItem>;
  limit: number;
  offset: number;
  total: number;
}

export interface PrePaymentPayload {
  ids: Array<string>;
  filter: {
    companies: Array<string>;
    licenseTypes: Array<number>;
    statuses: Array<number>;
    search: string;
    assigned: boolean;
    autoRenewal: boolean;
    endpoint: string;
    user: string;
    purchaseId: string;
  };
}

export interface PrePaymentAutoRenewPayload extends Partial<PrePaymentPayload> {
  enabled: boolean;
}

export interface ReleaseRequest {
  search?: Partial<FilterRequest>;
  itemsCount?: number;
  ids: Array<number>;
}

export interface AutoRenewalRequest {
  search?: Partial<FilterRequest>;
  ids: Array<string | number>;
  itemsCount?: number;
  enable: boolean;
}

export interface ManageRequest {
  licenseUsageId: number;
  licenseTypeId: number;
}

export interface AssignRequest {
  licenseTypeId: number;
  paid?: boolean;
}

export interface RemoveLicenseCommand {
  ownerId: string;
}

export interface LicenseStatistic {
  autoRenewable: number;
  expired: number;
  paid: number;
  total: number;
  trial: number;
}
export interface Balance {
  balance: number;
  hasDebt: boolean;
  subscriptionEnabled: boolean;
  subscriptionCurrencySign: string;
  minCommitment?: number;
}

export interface Debt {
  creditsLeft: number;
  creditsTotal: number;
  debtLeft: number;
  debtTotal: number;
}

export interface PayDebt {
  creditsLeft: number;
  debtTotal: number;
  paymentLink: string;
}

export type DebtInvoicesResponse = {
  creationDate: string;
  year: number;
  month: number;
  invoiceDetails: {
    licenseType: LicenseType;
    unitPrice: number;
    days: number;
    userCount: number;
    cost: number;
    discount: number;
    fullPrice: number;
  }[];
}[];

export interface Total {
  licenses: number;
  paid: number;
  trial: number;
  used: number;
  released: number;
  costToDate: Money;
  estimate: string;
  estimateWithDiscount: string;
  freeLicensesCount: number;
}

export type FreeLicense = {
  licenseTypeId: LicenseType;
  startDate: string;
  stopDate: string;
  count: number;
  period?: number;
};

export type PaidLicense = {
  licenseTypeId: LicenseType;
  quantity: number;
  price: number;
  days: number;
  expires: string;
  cost: number;
  costWithoutDiscount: number;
  discount: number;
};

export type NextPaymentData = {
  freeLicenseUsages: FreeLicense[];
  paidLicenseUsages: PaidLicense[];
};

export interface GroupDto {
  value: string;
  description: string;
  groupType: GroupType;
}

export interface FilterRequest {
  search: string;
  month: Date;
  company: string[];
  licenseTypes: number[];
  statusType: LicenseStatus;
}

export interface EndpointLicenseDto {
  type: EndpointType;
  id: string;
  name: string;
}

export interface EndpointDto {
  type: EndpointType;
  id: string;
}

export interface CheckLicenseCommand {
  ownerId: string;
  endpoint: EndpointDto;
}

export interface AssignLicenseCommand {
  ownerId: string;
  licenseType: LicenseType;
  startDate: string;
  endpoint: EndpointDto;
}

export interface LicensePool {
  ownerId: string;
  groupType: number;
  groupValue: string;
  licenseType: LicenseTypeDto;
  maxCount: number;
}

export interface LicensePoolLimit {
  groupType: number;
  groupValue: string;
  licenseTypeId: LicenseType;
  maxCount: number;
}

export interface SelectLicenseParams {
  licenseTypeId: LicenseType;
  hid: string;
  availableLicenseId: number | string;
}

export interface UsageHistoryLicense {
  licenseType: LicenseTypeData;
  user: {
    id: string;
    name: string;
  };
  company: {
    id: string;
    name: string;
  };
  endpoint: {
    name: string;
  };
  status: {
    id: number;
    displayName: string;
  };
  purchased: {
    id: string;
    date: string;
  };
  activated: string;
  expired: string;
  daysActive: number;
  paid: number;
  total: number;
}

export interface UsageHistoryResponse {
  totalAmountPaid: number;
  data: {
    data: Array<UsageHistoryLicense>;
    limit: number;
    offset: number;
    total: number;
  };
}

export interface UsageHistoryGroupedResponse {
  totalAmountPaid: number;
  data: {
    data: Array<UsageHistoryGroupedLicense>;
    limit: number;
    offset: number;
    total: number;
  };
}

export interface UsageHistoryGroupedLicense {
  company: {
    id: string;
    name: string;
  };
  licensesCount: number;
  paid: number;
}

export type GetAvailablePaidLicensesParams = {
  hid: string;
  licenseTypeIds: LicenseType[];
  offset?: number;
  limit?: number;
  skipError?: boolean;
  companyId?: string;
  userId?: string;
};

export type PostPaymentFilters = {
  month: string;
  company?: string[];
  licenseTypes?: LicenseType[];
  statusType?: LicenseStatus;
  endpointType?: EndpointType;
  endpointIds?: Array<string>;
  search?: string;
};

export type PostPaymentSortParams = {
  sortField: string;
  sortOrder: SortDirection;
};

export type PostPaymentParams = Partial<PostPaymentSortParams> & Partial<QueryPagingParams> & PostPaymentFilters;

export type PostPaymentURIParams = {
  month?: string;
  year?: string;
  company?: string[];
  statusType?: LicenseStatus;
  licenseTypes?: LicenseType[];
  endpointType?: EndpointType;
  endpointIds?: string[];
  search?: string;
};

export type PrePaymentParams = {
  filter: {
    companies?: Array<string>;
    licenseTypes?: LicenseType[];
    statuses?: PrePaymentLicenseStatus[];
    assigned?: AdditionalFilterValue.True | AdditionalFilterValue.False | boolean;
    autoRenewal?: AdditionalFilterValue.True | AdditionalFilterValue.False;
    endpoint?: string;
    user?: string;
    purchaseId?: string;
    search?: string;
  };
  sortField?: string;
  sortOrder?: SortDirection;
  limit?: number;
  offset?: number;
};

export interface PrePaymentTotals {
  totalCount: number;
  activeTrialCount: number;
  paidCount: number;
  expiredPaidCount: number;
  expiredTrialCount: number;
  autoRenewalDisabledCount: number;
  notAssignedCount: number;
  renewalDate: string;
  isFirstPurchase: boolean;
}

export enum Pool {
  UserPool,
  GlobalPool,
  CompanyPool,
  GappsPool
}

export type GroupByCompanyParams = {
  month: string;
  companies?: string[];
  licenseTypes?: LicenseType[];
};

export type GroupByCompanyRowResponse = {
  companyId: string;
  companyName: string;
  totalCost: number;
  storageUsageInBytes: number;
  cells: {
    [key: number]: {
      // LicenseType
      count: number;
      cost: number;
    };
  };
};

export type GroupByCompanyResponse = {
  licenseTypeColumns: LicenseType[];
  rows: GroupByCompanyRowResponse[];
};

export type GroupByCompanyLocalDataRow = {
  companyId: string;
  companyName: string;
  totalCost: string;
  storageUsageInBytes: string;
  cellsText: string[];
};

export type GroupByCompanyLocalData = {
  rows: GroupByCompanyLocalDataRow[];
  total: number;
};

export enum MBSLicenseKind {
  Unknown = 0,
  Backup = 1,
  Apps = 2,
  Rmm = 3,
  Connect = 4,
  Service = 5,
  Storage = 6,
  VmSocket = 7,
  Branding = 8,
  DeepInstinct = 9
}
